import React, { useState } from 'react';
import { FiCopy, FiCheck, FiMail, FiHeart } from 'react-icons/fi';
import toast from 'react-hot-toast';

const TONE_COLORS = {
  professional: '#3b82f6',
  warm: '#f59e0b',
  assertive: '#ef4444',
  casual: '#22c55e',
  diplomatic: '#8b5cf6'
};

function buildMailtoLink(text) {
  return `mailto:?body=${encodeURIComponent(text)}`;
}

function getSavedReplies() {
  try {
    return JSON.parse(localStorage.getItem('rc_saved_replies') || '[]');
  } catch { return []; }
}

function saveReply(reply) {
  const saved = getSavedReplies();
  // Avoid duplicates
  if (saved.some(r => r.content === reply.content)) return false;
  saved.unshift({ ...reply, savedAt: new Date().toISOString() });
  localStorage.setItem('rc_saved_replies', JSON.stringify(saved.slice(0, 50)));
  return true;
}

function unsaveReply(content) {
  const saved = getSavedReplies().filter(r => r.content !== content);
  localStorage.setItem('rc_saved_replies', JSON.stringify(saved));
}

function isReplySaved(content) {
  return getSavedReplies().some(r => r.content === content);
}

export default function ReplyOptions({ replies }) {
  const [copiedIdx, setCopiedIdx] = useState(null);
  const [savedState, setSavedState] = useState(() =>
    replies.map(r => isReplySaved(r.content))
  );

  const handleCopy = (text, idx) => {
    navigator.clipboard.writeText(text);
    setCopiedIdx(idx);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopiedIdx(null), 2000);
  };

  const handleSave = (reply, idx) => {
    if (savedState[idx]) {
      unsaveReply(reply.content);
      setSavedState(prev => prev.map((s, i) => i === idx ? false : s));
      toast.success('Removed from saved replies');
    } else {
      saveReply(reply);
      setSavedState(prev => prev.map((s, i) => i === idx ? true : s));
      toast.success('Saved to your replies!');
    }
  };

  const handleOpenInEmail = (text) => {
    window.open(buildMailtoLink(text), '_blank');
  };

  return (
    <div style={styles.container}>
      {replies.map((reply, idx) => {
        const color = TONE_COLORS[reply.tone] || 'var(--accent)';
        return (
          <div key={idx} style={styles.replyCard}>
            <div style={styles.replyHeader}>
              <span style={{ ...styles.toneBadge, background: color + '22', color }}>
                {reply.tone.charAt(0).toUpperCase() + reply.tone.slice(1)}
              </span>
              <div style={styles.actions}>
                <button
                  onClick={() => handleSave(reply, idx)}
                  style={{
                    ...styles.actionBtn,
                    color: savedState[idx] ? '#ef4444' : 'var(--text-muted)'
                  }}
                  title={savedState[idx] ? 'Remove from saved' : 'Save reply'}
                >
                  <FiHeart size={14} fill={savedState[idx] ? '#ef4444' : 'none'} />
                </button>
                <button
                  onClick={() => handleOpenInEmail(reply.content)}
                  style={styles.actionBtn}
                  title="Open in email client"
                >
                  <FiMail size={14} />
                  <span>Email</span>
                </button>
                <button onClick={() => handleCopy(reply.content, idx)} style={styles.actionBtn}>
                  {copiedIdx === idx ? <FiCheck size={14} color="var(--success)" /> : <FiCopy size={14} />}
                  <span>{copiedIdx === idx ? 'Copied!' : 'Copy'}</span>
                </button>
              </div>
            </div>
            <div style={styles.replyContent}>
              {reply.content.split('\n').map((line, i) => (
                <React.Fragment key={i}>
                  {line}
                  {i < reply.content.split('\n').length - 1 && <br />}
                </React.Fragment>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

const styles = {
  container: { display: 'flex', flexDirection: 'column', gap: 16 },
  replyCard: {
    background: 'var(--bg-secondary)',
    border: '1px solid var(--border)',
    borderRadius: 12,
    overflow: 'hidden'
  },
  replyHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    borderBottom: '1px solid var(--border)'
  },
  toneBadge: {
    padding: '4px 12px',
    borderRadius: 20,
    fontSize: 13,
    fontWeight: 600
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: 4
  },
  actionBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    background: 'none',
    border: 'none',
    color: 'var(--text-muted)',
    cursor: 'pointer',
    fontSize: 12,
    padding: '4px 8px',
    borderRadius: 6,
    transition: 'color 0.2s'
  },
  replyContent: {
    padding: '16px',
    color: 'var(--text-primary)',
    fontSize: 14,
    lineHeight: 1.7,
    whiteSpace: 'pre-wrap'
  }
};
